import type { LibraryQuery, LibrarySupervision } from "@screener/protocol";
import { amountLessOrEqual, parseAmountField } from "@/lib/amount";
import { AMOUNT_HINT } from "@/lib/labels";

/**
 * Фильтры панели библиотеки §9 (design.md stage7-library-panel, решение 2):
 * черновик — то, что набрано в полях; применённые фильтры — с которыми
 * получен показанный ответ. Чистые функции без React.
 */

/** Черновик — строки полей как есть. Пустая строка в бирже — «любая». */
export interface LibraryDraft {
  exchange: string;
  base: string;
  minUsd: string;
  maxUsd: string;
  supervision: LibrarySupervision;
}

/** Применённые фильтры — очищенные: монета обрезана, границы без разделителей. */
export type LibraryFilters = LibraryDraft;

export interface LibraryDraftErrors {
  minUsd?: string;
  maxUsd?: string;
  range?: string;
}

export const EMPTY_LIBRARY_DRAFT: LibraryDraft = {
  exchange: "",
  base: "",
  minUsd: "",
  maxUsd: "",
  supervision: "all",
};

/** Вся библиотека — выборка без фильтров, первый запрос экрана. */
export const EMPTY_LIBRARY_FILTERS: LibraryFilters = EMPTY_LIBRARY_DRAFT;

const RANGE_ERROR = "„От“ больше „до“";

export type ValidateDraftResult =
  | { ok: true; filters: LibraryFilters }
  | { ok: false; errors: LibraryDraftErrors };

/**
 * Проверяет черновик до запроса. Границы разбираются тем же разбором, что в
 * форме правила алерта, и сравниваются десятичными строками через `BigInt`,
 * без `Number`: сумма выше 2^53 иначе теряла бы разницу.
 */
export function validateLibraryDraft(draft: LibraryDraft): ValidateDraftResult {
  const errors: LibraryDraftErrors = {};
  const min = parseAmountField(draft.minUsd);
  const max = parseAmountField(draft.maxUsd);
  if (!min.ok) {
    errors.minUsd = AMOUNT_HINT;
  }
  if (!max.ok) {
    errors.maxUsd = AMOUNT_HINT;
  }
  if (min.ok && max.ok && min.value !== null && max.value !== null) {
    if (!amountLessOrEqual(min.value, max.value)) {
      errors.range = RANGE_ERROR;
    }
  }
  if (!(min.ok && max.ok) || errors.range !== undefined) {
    return { ok: false, errors };
  }
  return {
    ok: true,
    filters: {
      exchange: draft.exchange,
      base: draft.base.trim(),
      minUsd: min.value ?? "",
      maxUsd: max.value ?? "",
      supervision: draft.supervision,
    },
  };
}

/**
 * Применённые фильтры как объект запроса контракта: пустые поля и умолчание
 * `supervision=all` опускаются — демон отвергает пустое значение.
 */
export function queryOfFilters(filters: LibraryFilters): LibraryQuery {
  const query: LibraryQuery = {};
  if (filters.exchange !== "") {
    query.exchange = filters.exchange;
  }
  const base = filters.base.trim();
  if (base !== "") {
    query.base = base;
  }
  if (filters.minUsd !== "") {
    query.min_usd = filters.minUsd;
  }
  if (filters.maxUsd !== "") {
    query.max_usd = filters.maxUsd;
  }
  if (filters.supervision !== "all") {
    query.supervision = filters.supervision;
  }
  return query;
}

/**
 * Черновик расходится с применёнными фильтрами. Сравнивается очищенный вид:
 * пробелы вокруг монеты и другая запись той же суммы расхождением не
 * считаются. Негодный черновик расходится всегда — применённые фильтры
 * заведомо валидны.
 */
export function draftDiffersFrom(draft: LibraryDraft, applied: LibraryFilters): boolean {
  const result = validateLibraryDraft(draft);
  if (!result.ok) {
    return true;
  }
  const next = result.filters;
  return (
    next.exchange !== applied.exchange ||
    // Монета — без учёта регистра (spec `web-library-panel`): `btc` и `BTC`
    // дают одну выборку, и расхождением это не считается.
    next.base.toLowerCase() !== applied.base.toLowerCase() ||
    next.minUsd !== applied.minUsd ||
    next.maxUsd !== applied.maxUsd ||
    next.supervision !== applied.supervision
  );
}
